import { useEffect, useRef, useState, useCallback } from 'react';
import type { IFormSchema } from '~/types';

const CLIENT_URL = import.meta.env.VITE_FORMS_CLIENT_URL || 'http://localhost:3000';
const CLIENT_ORIGIN = new URL(CLIENT_URL).origin;
const DEBOUNCE_MS = 150;

interface PreviewIframeProps {
  schema: IFormSchema | null;
  schemaError?: string | null;
}

export const PreviewIframe = ({ schema, schemaError }: PreviewIframeProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [iframeHeight, setIframeHeight] = useState<number | null>(null);
  const pendingSchemaRef = useRef<IFormSchema | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sendSchema = useCallback((schemaToSend: IFormSchema) => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    iframe.contentWindow.postMessage(
      { type: 'preview:schema', schema: schemaToSend, timestamp: Date.now() },
      CLIENT_ORIGIN,
    );
  }, []);

  // Listen for messages from the client iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== CLIENT_ORIGIN) return;

      const { type } = event.data || {};

      if (type === 'preview:ready') {
        setIframeReady(true);
        // Send any pending schema that arrived before the iframe was ready
        if (pendingSchemaRef.current) {
          sendSchema(pendingSchemaRef.current);
          pendingSchemaRef.current = null;
        }
      }

      if (type === 'formHeight' && typeof event.data.height === 'number') {
        setIframeHeight(event.data.height);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendSchema]);

  // Send schema to iframe when it changes (debounced)
  useEffect(() => {
    if (!schema) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (iframeReady) {
        sendSchema(schema);
      } else {
        // Store for when iframe becomes ready
        pendingSchemaRef.current = schema;
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [schema, iframeReady, sendSchema]);

  if (!schema && schemaError) {
    return (
      <div className="text-center text-gray-500 py-12">
        <div className="text-red-600">
          <p className="font-medium mb-2">Schema Error:</p>
          <pre className="text-sm text-left bg-red-50 p-4 rounded">{schemaError}</pre>
        </div>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="text-center text-gray-500 py-12">
        <p>No valid schema</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {!iframeReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={`${CLIENT_URL}?mode=preview`}
        className="w-full border-0"
        style={{ height: iframeHeight ? `${iframeHeight}px` : '600px' }}
        title="Form Preview"
      />
    </div>
  );
};
